import React, { useState, useEffect, useRef } from 'react';
import { lmsAPI, mediaAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { BookOpen, Plus, Play, CheckCircle, Trash2, Edit2, Upload, Loader2, FileText, Video, Link2, HelpCircle, Presentation, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth-context';

const STATUS_COLORS = { enrolled: '#1E5A8A', in_progress: '#9A6B1A', completed: '#1F7A5A' };

const CATEGORIES = [
  { id: 'journalism', label: 'Journalism' },
  { id: 'editorial', label: 'Editorial' },
  { id: 'technical', label: 'Technical' },
  { id: 'leadership', label: 'Leadership & Management' },
  { id: 'legal', label: 'Legal & Compliance' },
  { id: 'hr', label: 'Human Resources' },
  { id: 'data', label: 'Data & Analytics' },
  { id: 'design', label: 'Design & Media' },
  { id: 'seo', label: 'SEO & Digital' },
  { id: 'sales', label: 'Sales & Ad Ops' },
  { id: 'finance', label: 'Finance' },
  { id: 'safety', label: 'Safety & Field Ops' },
  { id: 'onboarding', label: 'Onboarding' },
  { id: 'ethics', label: 'Ethics & Standards' },
];

const MAT_TYPES = [
  { id: 'video', label: 'Video', icon: Video },
  { id: 'pdf', label: 'PDF', icon: FileText },
  { id: 'link', label: 'Link', icon: Link2 },
  { id: 'quiz', label: 'Quiz', icon: HelpCircle },
  { id: 'slides', label: 'Slides', icon: Presentation },
];

const EMPTY_FORM = { title: '', description: '', category: 'journalism', instructor: '', duration_hours: 1, is_mandatory: false, target_roles: [], materials: [] };

export default function AdminLMS() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [matInput, setMatInput] = useState({ type: 'video', url: '', title: '' });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [allRes, myRes] = await Promise.all([
        lmsAPI.listCourses().catch(() => ({ data: { courses: [] } })),
        lmsAPI.myCourses().catch(() => ({ data: { courses: [] } })),
      ]);
      setCourses(allRes.data.courses || []);
      setMyCourses(myRes.data.courses || []);
    } catch { toast.error('Failed to load courses'); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setShowDialog(true);
  };

  const openEdit = (course) => {
    setEditingId(course.id);
    setForm({
      title: course.title || '',
      description: course.description || '',
      category: course.category || 'journalism',
      instructor: course.instructor || '',
      duration_hours: course.duration_hours || 1,
      is_mandatory: course.is_mandatory || false,
      target_roles: course.target_roles || [],
      materials: course.materials || [],
    });
    setShowDialog(true);
  };

  const saveCourse = async () => {
    if (!form.title) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await lmsAPI.updateCourse(editingId, form);
        toast.success('Course updated');
      } else {
        await lmsAPI.createCourse(form);
        toast.success('Course created');
      }
      setShowDialog(false);
      setForm({ ...EMPTY_FORM });
      setEditingId(null);
      loadAll();
    } catch { toast.error('Failed to save course'); }
    finally { setSaving(false); }
  };

  const deleteCourse = async (id) => {
    if (!window.confirm('Delete this course? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await lmsAPI.deleteCourse(id);
      toast.success('Course deleted');
      loadAll();
    } catch { toast.error('Failed to delete'); }
    finally { setDeleting(null); }
  };

  const enroll = async (courseId) => {
    try { await lmsAPI.enroll(courseId); toast.success('Enrolled!'); loadAll(); }
    catch { toast.error('Failed to enroll'); }
  };

  const updateProgress = async (courseId, pct) => {
    try { await lmsAPI.updateProgress(courseId, pct); loadAll(); }
    catch {}
  };

  const addMaterial = () => {
    if (!matInput.url && !matInput.title) return;
    setForm(f => ({ ...f, materials: [...f.materials, { ...matInput }] }));
    setMatInput({ type: 'video', url: '', title: '' });
  };

  const removeMaterial = (idx) => {
    setForm(f => ({ ...f, materials: f.materials.filter((_, j) => j !== idx) }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await mediaAPI.upload(formData);
      const url = res.data.url || res.data.file_url || '';
      const ext = file.name.split('.').pop().toLowerCase();
      const type = ['mp4', 'mov', 'avi', 'webm'].includes(ext) ? 'video'
        : ['pdf'].includes(ext) ? 'pdf'
        : ['pptx', 'ppt', 'key'].includes(ext) ? 'slides'
        : 'link';
      setForm(f => ({ ...f, materials: [...f.materials, { type, url, title: file.name }] }));
      toast.success(`Uploaded: ${file.name}`);
    } catch {
      toast.error('Upload failed — add a URL instead');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const MatIcon = ({ type }) => {
    const found = MAT_TYPES.find(t => t.id === type);
    const Icon = found?.icon || Link2;
    return <Icon className="w-3 h-3" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><BookOpen className="w-6 h-6 text-[#B89B5E]" /> Learning Management System</h1>
          <p className="text-sm text-[#5B616E] mt-1">Staff training courses, progress tracking, certifications</p>
        </div>
        <Button onClick={openCreate} className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Plus className="w-4 h-4 mr-2" /> New Course</Button>
      </div>

      <Tabs defaultValue="all">
        <TabsList><TabsTrigger value="all">All Courses ({courses.length})</TabsTrigger><TabsTrigger value="my">My Learning ({myCourses.length})</TabsTrigger></TabsList>

        <TabsContent value="all" className="mt-4">
          {loading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-44 rounded-xl" />)}</div> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {courses.map(c => {
                const myEnroll = myCourses.find(m => m.id === c.id)?.enrollment;
                return (
                  <Card key={c.id} className="bg-white dark:bg-[#1A1D23] border-[#E7E0D6] dark:border-white/[0.06] group">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm dark:text-white/90 truncate">{c.title}</h3>
                          <div className="flex gap-1 mt-0.5 flex-wrap">
                            <Badge variant="outline" className="text-[10px]">{c.category}</Badge>
                            {c.is_mandatory && <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Mandatory</Badge>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <span className="text-xs text-[#5B616E] dark:text-white/40">{c.duration_hours}h</span>
                          <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => openEdit(c)}>
                            <Edit2 className="w-3 h-3 text-[#5B616E]" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity" disabled={deleting === c.id} onClick={() => deleteCourse(c.id)}>
                            {deleting === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3 text-red-500" />}
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-[#5B616E] dark:text-white/50 line-clamp-2 mb-2">{c.description}</p>
                      {c.materials?.length > 0 && (
                        <div className="flex gap-1 mb-2 flex-wrap">
                          {c.materials.map((m, i) => (
                            <Badge key={i} variant="outline" className="text-[9px] gap-1 py-0">
                              <MatIcon type={m.type} /> {m.title || m.type}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {c.instructor && <p className="text-[10px] text-[#5B616E] dark:text-white/30 mb-2">Instructor: {c.instructor}</p>}
                      {myEnroll ? (
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-[#5B616E] dark:text-white/40">Progress</span>
                            <span style={{ color: STATUS_COLORS[myEnroll.status] }}>{myEnroll.progress_pct}%</span>
                          </div>
                          <div className="h-1.5 bg-[#E7E0D6] dark:bg-white/10 rounded-full">
                            <div className="h-1.5 rounded-full transition-all" style={{ width: `${myEnroll.progress_pct}%`, background: STATUS_COLORS[myEnroll.status] }} />
                          </div>
                          {myEnroll.status !== 'completed' && (
                            <div className="flex gap-1 mt-2">
                              {[25, 50, 75, 100].map(p => (
                                <Button key={p} size="sm" variant="outline" className="text-[10px] h-6 flex-1"
                                  onClick={() => updateProgress(c.id, p)}>{p}%</Button>
                              ))}
                            </div>
                          )}
                          {myEnroll.status === 'completed' && <p className="text-xs text-green-600 mt-2 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Completed</p>}
                        </div>
                      ) : (
                        <Button size="sm" onClick={() => enroll(c.id)} className="w-full text-xs bg-[#121316] dark:bg-white/10 text-white hover:bg-[#1B1D22]">
                          <Play className="w-3 h-3 mr-1" /> Enroll
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              {courses.length === 0 && (
                <div className="col-span-full text-center py-12 text-[#5B616E]">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p className="text-sm">No courses yet</p>
                  <p className="text-xs mt-1">Click "New Course" to create your first training course</p>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="my" className="mt-4">
          <div className="space-y-3">
            {myCourses.map(c => (
              <Card key={c.id} className="bg-white dark:bg-[#1A1D23] border-[#E7E0D6] dark:border-white/[0.06]">
                <CardContent className="pt-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm dark:text-white/90">{c.title}</h3>
                    <p className="text-xs text-[#5B616E] dark:text-white/40">{c.instructor ? `${c.instructor} · ` : ''}{c.duration_hours}h · {c.category}</p>
                  </div>
                  <div className="w-32">
                    <div className="h-2 bg-[#E7E0D6] dark:bg-white/10 rounded-full">
                      <div className="h-2 rounded-full" style={{ width: `${c.enrollment?.progress_pct || 0}%`, background: STATUS_COLORS[c.enrollment?.status] || '#5B616E' }} />
                    </div>
                    <p className="text-[10px] text-[#5B616E] dark:text-white/40 text-right mt-0.5">{c.enrollment?.progress_pct || 0}%</p>
                  </div>
                  <Badge style={{ background: `${STATUS_COLORS[c.enrollment?.status] || '#5B616E'}20`, color: STATUS_COLORS[c.enrollment?.status] || '#5B616E' }} className="text-[10px]">
                    {(c.enrollment?.status || 'enrolled').replace(/_/g, ' ')}
                  </Badge>
                </CardContent>
              </Card>
            ))}
            {myCourses.length === 0 && <p className="text-sm text-[#5B616E] text-center py-8">Not enrolled in any courses yet. Browse All Courses to enroll.</p>}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { if (!open) { setShowDialog(false); setEditingId(null); } }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? 'Edit Course' : 'Create Course'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-[#5B616E] mb-1 block">Title *</label>
              <Input placeholder="e.g. Investigative Journalism 101" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5B616E] mb-1 block">Description</label>
              <Textarea rows={3} placeholder="What will students learn?" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-[#5B616E] mb-1 block">Category</label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#5B616E] mb-1 block">Duration (hours)</label>
                <Input type="number" min={0.5} step={0.5} value={form.duration_hours} onChange={e => setForm(f => ({ ...f, duration_hours: parseFloat(e.target.value) || 1 }))} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#5B616E] mb-1 block">Instructor</label>
              <Input placeholder="Instructor name" value={form.instructor} onChange={e => setForm(f => ({ ...f, instructor: e.target.value }))} />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_mandatory} onChange={e => setForm(f => ({ ...f, is_mandatory: e.target.checked }))} className="rounded accent-[#B89B5E]" /> Mandatory for all staff
            </label>

            {/* Materials section */}
            <div className="border border-[#E7E0D6] dark:border-white/10 rounded-lg p-3">
              <p className="text-xs font-medium mb-2">Course Materials</p>
              {form.materials.length > 0 && (
                <div className="space-y-1.5 mb-3">
                  {form.materials.map((m, i) => (
                    <div key={i} className="flex items-center gap-2 bg-[#FBF7EF] dark:bg-white/[0.04] rounded-md px-2 py-1.5">
                      <MatIcon type={m.type} />
                      <Badge variant="outline" className="text-[9px] py-0">{m.type}</Badge>
                      <span className="text-xs flex-1 truncate">{m.title || m.url}</span>
                      {m.url && <a href={m.url} target="_blank" rel="noreferrer" className="text-[#1E5A8A] hover:underline"><Link2 className="w-3 h-3" /></a>}
                      <button onClick={() => removeMaterial(i)} className="text-red-500 hover:text-red-700"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add by URL */}
              <div className="flex gap-1 mb-2">
                <Select value={matInput.type} onValueChange={v => setMatInput(m => ({ ...m, type: v }))}>
                  <SelectTrigger className="w-24 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{MAT_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
                <Input className="text-xs h-8" placeholder="Title" value={matInput.title} onChange={e => setMatInput(m => ({ ...m, title: e.target.value }))} />
                <Input className="text-xs h-8 flex-1" placeholder="URL" value={matInput.url} onChange={e => setMatInput(m => ({ ...m, url: e.target.value }))} />
                <Button size="sm" variant="outline" className="h-8 text-xs px-3" onClick={addMaterial}>Add</Button>
              </div>

              {/* Upload file */}
              <input ref={fileRef} type="file" className="hidden" accept="video/*,.pdf,.pptx,.ppt,.key,.doc,.docx" onChange={handleFileUpload} />
              <Button variant="outline" size="sm" className="w-full text-xs h-8" disabled={uploading} onClick={() => fileRef.current?.click()}>
                {uploading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Uploading...</> : <><Upload className="w-3 h-3 mr-1" /> Upload File (video, PDF, slides)</>}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowDialog(false); setEditingId(null); }}>Cancel</Button>
            <Button onClick={saveCourse} disabled={saving} className="bg-[#121316] text-white hover:bg-[#1B1D22]">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingId ? 'Save Changes' : 'Create Course'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
